import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { camelCase } from 'change-case';
import { useCallback } from 'react';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ChatMessage } from './ChatTypes';
import { DataStream } from './DataStream';

export type CallChatApiOptions = {
    api: string,
    messages: ChatMessage[],
    onInitRequest?: (requestConfig: AxiosRequestConfig) => AxiosRequestConfig,
    onUpdate: (responseMessage: ChatMessage) => void,
    onFinish?: (responseMessage: ChatMessage) => void,
    onError?: (error: Error) => void,
};

const toCamelCaseKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map(item => toCamelCaseKeys(item));
    }
    else if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
                camelCase(key),
                toCamelCaseKeys(value)
            ])
        );
    }
    return obj;
};

export const useCallChatApi = () => {

    return useCallback(async (options: CallChatApiOptions) => {

        const { api, messages, onUpdate, onFinish, onError } = options;
        const onInitRequest = options.onInitRequest || (x => x);

        const requestBody = {
            messages: toCamelCaseKeys(messages),
            // Add stream flag if needed by the API
        };

        const requestConfig = onInitRequest({
            url: api,
            data: requestBody,
            responseType: 'text',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const assistantMessageId = uuid();
        let currentMessage: ChatMessage | null = null;

        try {
            // Note: Axios in browser uses XMLHttpRequest. `onDownloadProgress` is key for streaming.
            await axios({
                method: 'POST',
                ...requestConfig,
                onDownloadProgress: (progressEvent: any) => {
                    const xhr = progressEvent.target as XMLHttpRequest;
                    if (!xhr) {
                        console.warn('AxiosProgressEvent target is not available.');
                        return;
                    }
                    const message = DataStream.parse(xhr.responseText, assistantMessageId);
                    onUpdate(message);
                    currentMessage = message;
                },
            });

            // After axios promise resolves (request finished successfully)
            // The stream is now fully processed in the onDownloadProgress callback

            if (onFinish) {
                onFinish({ ...currentMessage, streaming: false });
            }

        }
        catch (error) {
            const err = error as AxiosError;
            // console.error('Error calling chat API:', err.message, err.response?.data, err.config);
            const errorMessage = `API Error: ${err.message}${err.response?.status ? ` (Status: ${err.response.status})` : ''}`;
            if (onError) {
                onError(new Error(errorMessage));
            }
            else {
                // Fallback error handling if no onError prop is provided
                console.error(errorMessage, err);
            }
        }

    }, []);
};
